import { appendScript } from "~/recipal-app/controllers/shared/requests";
import { Confirm } from "~/jose_modern/confirm";
import { Flash } from "~/jose_modern/flash";
import { displayError } from "~/jose_modern/field_with_errors";
import { once } from "~/jose_modern/once";
import { Modal } from "~/jose_modern/modal";
import { initializeSingleLotCodeTypeahead } from "../lot_code_typeahead";
import {
  LOT_CODE_MISSING_CLASS,
  isLotCodeRequired,
  markInputMissing,
  reportMissingLotCodes,
} from "../lot_code_validation";
import { URL_QUERY_PARAMS, CHANGED_ACTUAL_CLASS, startSpinner, stopSpinner } from "./common";

const HAS_PENDING_CHANGES_CLASS = "has-pending-changes";
const LOT_CODE_SELECTOR = ".lot-code";
const QUANTITY_SELECTOR = ".inventory-quantity";
const UNIT_SELECTOR = ".inventory-unit";
const LOT_RESET_QUANTITY_SELECTOR = ".lot-reset-quantity";
const LOT_RESET_UNIT_SELECTOR = ".lot-reset-unit";
const VISIBLE_LOT_RESET_CONTAINER =
  "[data-lot-code-reset-by-lot]:not(.is-hidden) [data-lot-code-reset-rows]";
const FIELD_SELECTORS = `${QUANTITY_SELECTOR}, ${UNIT_SELECTOR}, ${LOT_CODE_SELECTOR}, ${LOT_RESET_QUANTITY_SELECTOR}, ${LOT_RESET_UNIT_SELECTOR}`;

const ACTION_TYPE_PARAM = "adjustment_type";
const PULSE_REMINDER_DELAY_MS = 4000;

let changeHandlers = {};
let hasUnsavedChanges = false;
let isConfirmDialogActive = false;
let previousHasUnsavedChanges = false;
let pulseTimeoutId = null;
let pulseReminderTimeoutId = null;

// =============================================================================
// FLOATING WIDGET MANAGEMENT
// =============================================================================

const pulseToggleButtonOnce = (strength = "subtle") => {
  const btn = document.getElementById("floating-form-toggle");
  if (!btn) return;

  // If a pulse is in progress, don't re-trigger; let the current one finish
  if (pulseTimeoutId) return;

  btn.classList.remove("is-pulsing", "is-pulsing-strong");
  // Force reflow so animation can restart when class is re-added
  // eslint-disable-next-line no-unused-expressions
  btn.offsetWidth;
  const className = strength === "strong" ? "is-pulsing-strong" : "is-pulsing";
  btn.classList.add(className);
  // Prefer animationend to know exactly when the CSS animation completes
  const onEnd = (ev) => {
    if (
      ev.animationName === "floating-button-pulse" ||
      ev.animationName === "floating-button-pulse-strong"
    ) {
      btn.classList.remove("is-pulsing", "is-pulsing-strong");
      if (pulseTimeoutId) {
        clearTimeout(pulseTimeoutId);
        pulseTimeoutId = null;
      }
    }
  };
  btn.addEventListener("animationend", onEnd, { once: true });

  // Fallback cleanup (covers rare cases where animationend might not fire)
  pulseTimeoutId = setTimeout(() => {
    btn.classList.remove("is-pulsing", "is-pulsing-strong");
    pulseTimeoutId = null;
  }, 2200);
};

const clearPulseTimers = () => {
  if (pulseTimeoutId) {
    clearTimeout(pulseTimeoutId);
    pulseTimeoutId = null;
  }
  if (pulseReminderTimeoutId) {
    clearTimeout(pulseReminderTimeoutId);
    pulseReminderTimeoutId = null;
  }
};

const schedulePulseReminder = () => {
  const floatingComponent = document.getElementById("floating-component");
  const toggleButton = document.getElementById("floating-form-toggle");

  if (!floatingComponent || !toggleButton) return;

  if (!previousHasUnsavedChanges || floatingComponent.classList.contains("is-active")) {
    clearPulseTimers();
    return;
  }

  if (pulseReminderTimeoutId) clearTimeout(pulseReminderTimeoutId);
  pulseReminderTimeoutId = setTimeout(() => {
    const stillDirty = previousHasUnsavedChanges;
    const isOpen = floatingComponent.classList.contains("is-active");
    if (stillDirty && !isOpen) {
      pulseToggleButtonOnce();
      schedulePulseReminder();
    }
  }, PULSE_REMINDER_DELAY_MS);
};

export const updateFloatingWidget = (hasUnsavedChangesParam) => {
  hasUnsavedChanges = hasUnsavedChangesParam;
  const floatingComponent = document.getElementById("floating-component");
  const toggleButton = document.getElementById("floating-form-toggle");

  if (!floatingComponent || !toggleButton) {
    return;
  }

  const isMobileOrTablet = window.innerWidth < 1024;

  // When we transition to having unsaved changes, do a strong pulse
  if (hasUnsavedChanges && !previousHasUnsavedChanges) {
    pulseToggleButtonOnce("strong");
  }

  if (isMobileOrTablet) {
    // Mobile/Tablet: show/hide the button based on unsaved changes
    if (hasUnsavedChanges) {
      floatingComponent.style.display = "";
      floatingComponent.style.visibility = "";
      toggleButton.style.display = "";
      toggleButton.style.visibility = "";
      // Also schedule reminder pulses when dirty and not open
      schedulePulseReminder();
    } else {
      toggleButton.style.display = "none";
      // Also ensure the well is not active when hidden
      floatingComponent.classList.remove("is-active");
      toggleButton.classList.remove("is-active");
    }
  } else {
    // Desktop: always show the widget, but do not auto-open when unsaved changes
    floatingComponent.style.display = "";
    floatingComponent.style.visibility = "";
    toggleButton.style.display = "";
    toggleButton.style.visibility = "";

    if (hasUnsavedChanges) {
      schedulePulseReminder();
    } else {
      floatingComponent.classList.remove("is-active");
      toggleButton.classList.remove("is-active");
      clearPulseTimers();
    }
  }

  previousHasUnsavedChanges = hasUnsavedChanges;

  requestAnimationFrame(() => {
    document.dispatchEvent(new CustomEvent("floating-widget-visibility-changed"));
  });
};

export const initializeFloatingWidgetForm = () => {
  once(document.body, "floating-widget-globals", () => {
    const onToggle = (e) => {
      const toggleButton = e.target.closest("#floating-form-toggle");

      if (toggleButton) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Use setTimeout to ensure this runs after any other handlers
        setTimeout(() => {
          const floatingComponent = document.getElementById("floating-component");
          const floatingFormToggle = document.getElementById("floating-form-toggle");

          if (!floatingComponent || !floatingFormToggle) {
            return;
          }

          floatingComponent.classList.toggle("is-active");
          floatingFormToggle.classList.toggle("is-active");

          document.dispatchEvent(new CustomEvent("floating-toggle-clicked"));
        }, 10);
      }
    };

    const onClose = (e) => {
      const isMobileOrTablet = window.innerWidth < 1024;
      if (!isMobileOrTablet) return; // Only enable click-outside-to-close on mobile/tablet

      const floatingComponent = document.getElementById("floating-component");

      if (floatingComponent && floatingComponent.classList.contains("is-active")) {
        if (!floatingComponent.contains(e.target)) {
          floatingComponent.classList.remove("is-active");
          const floatingFormToggle = document.getElementById("floating-form-toggle");
          if (floatingFormToggle) floatingFormToggle.classList.remove("is-active");
        }
      }
    };

    document.addEventListener(
      "click",
      onToggle,
      true // Use capture phase
    );
    document.addEventListener("click", onClose);
  });
};

// =============================================================================
// CHANGE TRACKING
// =============================================================================

const createFieldChangeHandler = (onChangeCallback) => {
  return (e) => {
    const target = e.target;
    if (!target.matches(FIELD_SELECTORS)) return;
    if (target.closest("[data-additional-lot-code]")) return;

    const originalValue = target.dataset.originalValue || "";
    const row = target.closest("tr");
    const shouldMarkChanged = target.value !== originalValue;

    if (shouldMarkChanged) {
      target.classList.add(CHANGED_ACTUAL_CLASS);
      if (row) row.classList.add(HAS_PENDING_CHANGES_CLASS);
    } else {
      target.classList.remove(CHANGED_ACTUAL_CLASS);
      if (row && row.querySelectorAll(`.${CHANGED_ACTUAL_CLASS}`).length === 0) {
        row.classList.remove(HAS_PENDING_CHANGES_CLASS);
      }
    }
    if (onChangeCallback) onChangeCallback();
  };
};

export const createChangeTracker = (context) => {
  const fields = document.querySelectorAll(FIELD_SELECTORS);
  fields.forEach((field) => {
    field.dataset.originalValue = field.value;
  });

  ["change", "input", "keydown"].forEach((eventType) => {
    if (changeHandlers[eventType]) {
      document.removeEventListener(eventType, changeHandlers[eventType]);
    }
  });

  if (changeHandlers["unitChange"]) {
    document.removeEventListener("change", changeHandlers["unitChange"]);
  }

  const onChangeCallback = () => {
    hasUnsavedChanges = document.querySelectorAll(`.${CHANGED_ACTUAL_CLASS}`).length > 0;
    updateFloatingWidget(hasUnsavedChanges);
  };

  ["change", "input"].forEach((eventType) => {
    changeHandlers[eventType] = createFieldChangeHandler(onChangeCallback);
    document.addEventListener(eventType, changeHandlers[eventType]);
  });

  changeHandlers["keydown"] = async (e) => {
    if (e.key === "Enter" && e.target.matches(FIELD_SELECTORS)) {
      if (e.target.classList.contains("lot-code")) {
        const dropdown = e.target.parentElement.querySelector(".autocomplete-suggestions");
        if (dropdown && !dropdown.classList.contains("is-hidden")) return;
      }
      e.preventDefault();
      e.stopPropagation();

      const floatingComponent = document.getElementById("floating-component");
      const toggleButton = document.getElementById("floating-form-toggle");

      if (floatingComponent && toggleButton && !floatingComponent.classList.contains("is-active")) {
        floatingComponent.classList.add("is-active");
        toggleButton.classList.add("is-active");
        document.dispatchEvent(new CustomEvent("floating-toggle-clicked"));
      } else {
        await _performInventorySubmission({ context, actionType: "adjust" });
      }
    }
  };
  document.addEventListener("keydown", changeHandlers["keydown"]);

  // On reset page, if unit is changed but quantity is original value, clear quantity.
  // This is for an edge case where a user changed one row's quantity and forgot to change its unit,
  // then they make some other change and submit it; they probably would not intend to change 10 pounds to 10 grams for example.
  const isResetPage = context.adjustmentType === "reset";
  if (isResetPage) {
    const handleUnitChangeForResetPage = (e) => {
      const unitSelect = e.target;
      if (!unitSelect.matches(UNIT_SELECTOR)) return;

      const row = unitSelect.closest("tr");
      if (row?.querySelector(VISIBLE_LOT_RESET_CONTAINER)) return;

      const originalUnitValue = unitSelect.dataset.originalValue || "";
      if (unitSelect.value === originalUnitValue) return;

      const quantityInput = row.querySelector(QUANTITY_SELECTOR);

      // Only clear if quantity was left at its original value
      const originalQuantityValue = quantityInput.dataset.originalValue || "";
      const hasUserEditedQuantity = quantityInput.value !== originalQuantityValue;
      if (hasUserEditedQuantity) return;

      const wasValueChanged = quantityInput.value !== "";
      quantityInput.value = "";
      quantityInput.focus();
      if (wasValueChanged) {
        quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    };

    changeHandlers["unitChange"] = handleUnitChangeForResetPage;
    document.addEventListener("change", changeHandlers["unitChange"]);
  }

  // Inline lot code validation hints
  if (changeHandlers["lotCodeBlur"]) {
    document.removeEventListener("blur", changeHandlers["lotCodeBlur"], true);
  }
  if (changeHandlers["lotCodeInput"]) {
    document.removeEventListener("input", changeHandlers["lotCodeInput"]);
  }

  changeHandlers["lotCodeBlur"] = (e) => {
    if (!e.target.matches(QUANTITY_SELECTOR)) return;
    if (!isLotCodeRequired()) return;

    const value = parseFloat(e.target.value);
    if (!value || value === 0) return;

    const row = e.target.closest("tr");
    if (!row) return;
    const lotCodeInput = row.querySelector(LOT_CODE_SELECTOR);
    if (lotCodeInput && !lotCodeInput.value.trim()) {
      lotCodeInput.classList.add(LOT_CODE_MISSING_CLASS);
    }
  };
  document.addEventListener("blur", changeHandlers["lotCodeBlur"], true);

  changeHandlers["lotCodeInput"] = (e) => {
    if (!e.target.matches(LOT_CODE_SELECTOR)) return;
    if (e.target.value.trim()) {
      e.target.classList.remove(LOT_CODE_MISSING_CLASS);
    }
  };
  document.addEventListener("input", changeHandlers["lotCodeInput"]);
};

// =============================================================================
// FORM SUBMISSION
// =============================================================================

const buildSubmissionPayload = ({ context, actionType }) => {
  const { resource, resourceSingle } = context;

  const data = {
    [resource]: [],
    notes: "",
    [ACTION_TYPE_PARAM]: actionType,
  };

  const changedElements = document.querySelectorAll(`.${CHANGED_ACTUAL_CLASS}`);

  const processedRows = new Set();
  changedElements.forEach((element) => {
    const row = element.closest("tr");
    if (!row || processedRows.has(row)) return;

    const itemId = element.dataset.recordId;
    if (!itemId) return;
    processedRows.add(row);

    const quantityElement = row.querySelector(`#inventory-quantity-${itemId}`);
    const unitElement = row.querySelector(`#inventory-unit-${itemId}`);
    if (!quantityElement || !unitElement) return;

    const quantity = parseFloat(quantityElement.value);
    if (isNaN(quantity)) return;

    const itemData = {
      id: itemId,
      [resourceSingle]: { unit: unitElement.value },
    };

    if (actionType === "reset") {
      const lotContainer = row.querySelector(VISIBLE_LOT_RESET_CONTAINER);
      let quantities = [];
      if (lotContainer) {
        const lotUnitSelect = lotContainer.querySelector(LOT_RESET_UNIT_SELECTOR);
        if (lotUnitSelect) itemData[resourceSingle].unit = lotUnitSelect.value;

        lotContainer.querySelectorAll(LOT_RESET_QUANTITY_SELECTOR).forEach((input) => {
          quantities.push({
            lotCode: input.dataset.lotCode,
            quantity: parseFloat(input.value),
            persistedLotCodeId: input.dataset.persistedLotCodeId,
          });
        });
      } else {
        quantities = [{ quantity }];
      }
      itemData[resourceSingle].quantities = quantities;
    } else {
      const primaryLotCodeInput = row.querySelector(".lot-code");
      const primaryLotCode = primaryLotCodeInput?.value.trim();

      const quantities = [{ lotCode: primaryLotCode, quantity }];

      row.querySelectorAll("[data-additional-lot-code]").forEach((container) => {
        const codeInput = container.querySelector("[data-lot-code]");
        const quantityInput = container.querySelector("[data-lot-quantity]");
        const lotCode = codeInput.value.trim();
        const qty = parseFloat(quantityInput.value);

        quantities.push({ lotCode, quantity: qty });
      });

      itemData[resourceSingle].quantities = quantities;
    }
    data[resource].push(itemData);
  });

  const notesInput = document.getElementById("inventory-adjustment-notes");
  data.notes = notesInput?.value || "";

  return data;
};

const buildSubmissionFormData = ({ context, collectedData, actionType }) => {
  const { resource, resourceSingle } = context;
  const formData = new FormData();

  collectedData[resource].forEach((item, index) => {
    formData.append(`${resource}[${index}][id]`, item.id);
    Object.entries(item[resourceSingle]).forEach(([key, value]) => {
      if (key === "quantities") {
        value.forEach((quantityChange, quantityIndex) => {
          if (quantityChange.quantity !== undefined) {
            formData.append(
              `${resource}[${index}][${resourceSingle}][quantities][${quantityIndex}][quantity]`,
              quantityChange.quantity
            );
          }
          if (quantityChange.lotCode) {
            formData.append(
              `${resource}[${index}][${resourceSingle}][quantities][${quantityIndex}][lot_code]`,
              quantityChange.lotCode
            );
          }
          if (quantityChange.persistedLotCodeId) {
            formData.append(
              `${resource}[${index}][${resourceSingle}][quantities][${quantityIndex}][persisted_lot_code_id]`,
              quantityChange.persistedLotCodeId
            );
          }
        });
      } else {
        formData.append(`${resource}[${index}][${resourceSingle}][${key}]`, value);
      }
    });
  });

  formData.append("notes", collectedData.notes);
  formData.append(ACTION_TYPE_PARAM, actionType);

  if (actionType !== "reset") {
    document.querySelectorAll("[data-metadata-field]").forEach((el) => {
      if (el.value) {
        const key = el.dataset.metadataField;
        formData.append(key, el.value);
      }
    });
  }

  const searchParams = new URLSearchParams(window.location.search);
  URL_QUERY_PARAMS.forEach((param) => {
    const value = searchParams.get(param);
    if (value) formData.append(param, value);
  });

  return formData;
};

const clearLotCodeErrors = () => {
  document.querySelectorAll("[data-lot-code], [data-lot-quantity]").forEach((input) => {
    if (input._tippy) {
      input._tippy.destroy();
    }
  });
};

const validateAdditionalLotCodes = () => {
  clearLotCodeErrors();

  let firstInvalid = null;
  const changedRows = document.querySelectorAll(`.${HAS_PENDING_CHANGES_CLASS}`);

  changedRows.forEach((row) => {
    row.querySelectorAll("[data-additional-lot-code]").forEach((container) => {
      const qtyInput = container.querySelector("[data-lot-quantity]");
      const codeInput = container.querySelector("[data-lot-code]");
      if (!qtyInput || !codeInput) return;

      const qty = parseFloat(qtyInput.value);
      const lotCode = codeInput.value.trim();

      if (!lotCode) {
        displayError(codeInput, "Lot code is required for additional lot codes");
        firstInvalid ??= codeInput;
      }

      if (isNaN(qty)) {
        displayError(qtyInput, "Quantity is required");
        firstInvalid ??= qtyInput;
      }
    });
  });

  if (firstInvalid) {
    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  return firstInvalid === null;
};

const validateRequiredLotCodes = () => {
  if (!isLotCodeRequired()) return true;

  const changedRows = document.querySelectorAll(`.${HAS_PENDING_CHANGES_CLASS}`);
  const invalidInputs = [];

  changedRows.forEach((row) => {
    const primaryLotCode = row.querySelector(LOT_CODE_SELECTOR);
    if (!primaryLotCode) return;

    if (!primaryLotCode.value.trim()) {
      invalidInputs.push(markInputMissing(primaryLotCode, row, { highlight: true }));
    }

    row.querySelectorAll("[data-additional-lot-code]").forEach((container) => {
      const codeInput = container.querySelector("[data-lot-code]");
      if (codeInput && !codeInput.value.trim()) {
        invalidInputs.push(markInputMissing(codeInput, row));
      }
    });
  });

  return reportMissingLotCodes(
    invalidInputs,
    "Lot codes are required for all adjustments per your account settings. Please fill in the highlighted fields."
  );
};

const _performInventorySubmission = async ({ context, actionType, button = null }) => {
  if (document.querySelectorAll(`.${CHANGED_ACTUAL_CLASS}`).length === 0) {
    alert("No changes detected. Please make changes before submitting.");
    return;
  }

  if (actionType !== "reset" && !validateRequiredLotCodes()) return;
  if (actionType !== "reset" && !validateAdditionalLotCodes()) return;
  if (actionType === "reset" && !validateResetLotCodes()) return;
  if (actionType === "reset" && isLotCodeRequired() && !validateResetRequiresLots()) return;

  const collectedData = buildSubmissionPayload({ context, actionType });
  const items = collectedData[context.resource];
  if (items.length === 0) {
    alert("Please enter valid quantities to update.");
    return;
  }

  const { singular, singularPossessive, pluralPossessive } = context.userFacingText;
  const itemText = items.length === 1 ? singularPossessive : pluralPossessive;
  const confirmed = await showConfirmAdjustment({
    actionType,
    itemCount: items.length,
    itemTypeText: itemText,
  });
  if (!confirmed) return;

  const endpoint = actionType === "reset" ? context.endpoints.reset : context.endpoints.adjust;
  const formData = buildSubmissionFormData({ context, collectedData, actionType });

  if (button) button.classList.add("is-loading");

  startSpinner();

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { Accept: "text/javascript" },
      body: formData,
    });

    if (response.ok) {
      const js = await response.text();
      if (js.includes("replace_partial")) {
        appendScript(js);
        hasUnsavedChanges = false;
        updateFloatingWidget(hasUnsavedChanges);
        document.querySelectorAll("form[data-warn-unsaved-changes]").forEach((warnForm) => {
          warnForm.reset();
          warnForm.querySelectorAll("input, textarea, select").forEach((input) => {
            input.dispatchEvent(new Event("input", { bubbles: true }));
          });
        });
      } else {
        appendScript(js);
      }
    } else {
      const errorMsg = `Server error: ${response.status} ${response.statusText}`;
      console.error(errorMsg);
      alert(`Error updating ${singular}: ${errorMsg}. Please try again.`);
    }
  } catch (error) {
    console.error("Error submitting form:", error);
    alert(`Error updating ${singular}. Please try again.`);
    stopSpinner();
  } finally {
    stopSpinner();
    if (button) button.classList.remove("is-loading");
  }
};

const showConfirmAdjustment = async ({ actionType, itemCount, itemTypeText }) => {
  if (isConfirmDialogActive) {
    return false;
  }

  isConfirmDialogActive = true;

  const actionText = actionType === "reset" ? "reset" : "adjust";
  const confirmMessage = `You are about to ${actionText} ${itemCount} ${itemTypeText} inventory levels. Are you sure?`;

  try {
    const confirmation = await Confirm.confirm(confirmMessage);
    return Boolean(confirmation && confirmation.response);
  } catch (error) {
    console.error("Error showing confirmation dialog:", error);
    return confirm(confirmMessage);
  } finally {
    isConfirmDialogActive = false;
  }
};

export const createSubmitHandler = (context) => {
  return async (e) => {
    const button = e.target.closest(".inventory-submit-button");
    if (!button) return;

    const actionType = button.dataset.actionType;
    if (!actionType) {
      console.error("No action type found on button:", button);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    await _performInventorySubmission({ context, actionType, button });
  };
};

export const setupUnitChangeModal = () => {
  document.addEventListener("change", (e) => {
    if (e.target.matches(".js-unit-reset")) {
      const modal = document.getElementById("inventory-unit-change-modal");
      if (modal) {
        Modal.show("#inventory-unit-change-modal");
      }
    }
  });
};

// =============================================================================
// MULTIPLE LOT CODES
// =============================================================================

export const initializeLotCodeButtons = () => {
  once(document.body, "lot-code-add-remove", (body) => {
    body.addEventListener("click", (e) => {
      const addBtn = e.target.closest("[data-add-lot-code]");
      if (addBtn) {
        e.preventDefault();
        const recordId = addBtn.dataset.recordId;
        const template = document.querySelector(`[data-lot-code-template="${recordId}"]`);
        const container = document.querySelector(`[data-lot-code-rows="${recordId}"]`);
        if (!template || !container) return;

        const clone = template.content.cloneNode(true);
        const typeaheadInput = clone.querySelector('[data-provide="typeahead"]');
        container.appendChild(clone);

        const rows = container.querySelectorAll("[data-additional-lot-code]");
        const lastRow = rows[rows.length - 1];

        if (typeaheadInput) initializeSingleLotCodeTypeahead(typeaheadInput);

        const unitSelect = document.querySelector(`#inventory-unit-${recordId}`);
        const unitDisplay = lastRow?.querySelector("[data-lot-unit-display]");
        if (unitSelect && unitDisplay) unitDisplay.textContent = unitSelect.value;

        const qtyInput = lastRow?.querySelector("[data-lot-quantity]");
        if (qtyInput) qtyInput.focus();

        return;
      }

      const removeBtn = e.target.closest("[data-remove-lot-code]");
      if (removeBtn) {
        e.preventDefault();
        const row = removeBtn.closest("[data-additional-lot-code]");
        if (row) {
          const codeInput = row.querySelector("[data-lot-code]");
          if (codeInput?._tippy) codeInput._tippy.destroy();
          row.remove();
        }
      }
    });

    body.addEventListener("change", (e) => {
      if (!e.target.matches(UNIT_SELECTOR)) return;
      const container = document.querySelector(
        `[data-lot-code-rows="${e.target.dataset.recordId}"]`
      );
      if (!container) return;
      container.querySelectorAll("[data-lot-unit-display]").forEach((el) => {
        el.textContent = e.target.value;
      });
    });
  });
};

// =============================================================================
// RESET BY LOT CODES
// =============================================================================

const updateLotTotal = (inventoryId) => {
  const container = document.querySelector(`[data-lot-code-reset-rows="${inventoryId}"]`);
  const totalEl = document.querySelector(
    `[data-lot-code-reset-total="${inventoryId}"] [data-lot-total-value]`
  );
  if (!container || !totalEl) return;

  let sum = 0;
  container.querySelectorAll(LOT_RESET_QUANTITY_SELECTOR).forEach((input) => {
    const val = parseFloat(input.value);
    if (!isNaN(val)) sum += val;
  });
  totalEl.textContent = parseFloat(sum.toFixed(4));
};

const handleByLotUnitChange = (e) => {
  if (!e.target.matches(LOT_RESET_UNIT_SELECTOR)) return;

  const unitSelect = e.target;
  const inventoryId = unitSelect.dataset.recordId;
  const container = document.querySelector(`[data-lot-code-reset-rows="${inventoryId}"]`);
  if (!container) return;

  const factors = JSON.parse(container.dataset.unitFactors || "{}");
  const previousUnit = unitSelect.dataset.previousUnit || unitSelect.dataset.originalUnit;
  const originalUnit = unitSelect.dataset.originalUnit;
  const newUnit = unitSelect.value;
  const oldFactor = factors[previousUnit];
  const newFactor = factors[newUnit];

  if (oldFactor && newFactor && previousUnit !== newUnit) {
    container.querySelectorAll(LOT_RESET_QUANTITY_SELECTOR).forEach((input) => {
      // If the user changed something, leave it as is
      const isChanged = input.classList.contains(CHANGED_ACTUAL_CLASS);
      if (isChanged) return;

      const currentValue = parseFloat(input.value);
      if (isNaN(currentValue)) return;

      const converted =
        newUnit === originalUnit
          ? input.dataset.initialValue // Avoid potential rounding issues if we're back to the initial value
          : parseFloat(((currentValue * oldFactor) / newFactor).toFixed(4));
      input.value = converted;
      input.dataset.originalValue = converted;
    });
  }

  unitSelect.dataset.previousUnit = newUnit;

  const totalContainer = document.querySelector(`[data-lot-code-reset-total="${inventoryId}"]`);
  if (totalContainer) {
    totalContainer.dataset.lotUnit = newUnit;
    const totalUnitEl = totalContainer.querySelector("[data-lot-total-unit]");
    if (totalUnitEl) totalUnitEl.textContent = newUnit;
  }

  updateLotTotal(inventoryId);
};

const handleToggleByLot = (e) => {
  const toggle = e.target.closest("[data-toggle-lots]");
  if (!toggle) return;
  e.preventDefault();

  const inventoryId = toggle.dataset.recordId;
  const resetByLotContainer = document.querySelector(
    `[data-lot-code-reset-by-lot="${inventoryId}"]`
  );
  const rowsContainer = resetByLotContainer.querySelector(`[data-lot-code-reset-rows]`);
  const totalContainer = resetByLotContainer.querySelector(`[data-lot-code-reset-total]`);
  const quantityAndUnit = document.querySelector(`[data-quantity-unit="${inventoryId}"]`);
  const row = toggle.closest("tr");

  if (!resetByLotContainer) return;

  const isCollapsed =
    resetByLotContainer.classList.contains("is-hidden") ||
    resetByLotContainer.classList.contains("is-hidden-tablet");

  if (isCollapsed) {
    resetByLotContainer.classList.remove("is-hidden", "is-hidden-tablet");
    if (quantityAndUnit) quantityAndUnit.classList.add("is-hidden");

    const totalUnitEl = totalContainer.querySelector("[data-lot-total-unit]");
    if (totalUnitEl) totalUnitEl.textContent = totalContainer.dataset.lotUnit || "";

    updateLotTotal(inventoryId);
  } else {
    resetByLotContainer.classList.add("is-hidden");
    if (quantityAndUnit) quantityAndUnit.classList.remove("is-hidden");

    const quantityInput = row?.querySelector(QUANTITY_SELECTOR);
    if (quantityInput) {
      quantityInput.value = quantityInput.dataset.originalValue || "";
      quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
    }

    const lotUnitSelect = rowsContainer.querySelector(LOT_RESET_UNIT_SELECTOR);
    if (lotUnitSelect) {
      lotUnitSelect.value = lotUnitSelect.dataset.originalUnit;
      lotUnitSelect.dataset.previousUnit = lotUnitSelect.dataset.originalUnit;
      lotUnitSelect.classList.remove(CHANGED_ACTUAL_CLASS);
    }

    rowsContainer.querySelectorAll(LOT_RESET_QUANTITY_SELECTOR).forEach((input) => {
      const initialValue = input.dataset.initialValue || input.dataset.originalValue || "";
      input.value = initialValue;
      input.dataset.originalValue = initialValue;
      input.classList.remove(CHANGED_ACTUAL_CLASS);
    });

    if (row && row.querySelectorAll(`.${CHANGED_ACTUAL_CLASS}`).length === 0) {
      row.classList.remove(HAS_PENDING_CHANGES_CLASS);
    }
  }
};

export const initializeResetByLots = () => {
  once(document.body, "reset-by-lots", (body) => {
    body.addEventListener("input", (e) => {
      if (!e.target.matches(LOT_RESET_QUANTITY_SELECTOR)) return;
      const container = e.target.closest("[data-lot-code-reset-rows]");
      if (container) updateLotTotal(container.dataset.lotCodeResetRows);
    });

    body.addEventListener("change", handleByLotUnitChange);

    body.addEventListener("click", handleToggleByLot);
  });
};

const validateResetLotCodes = () => {
  let firstInvalid = null;
  const changedRows = document.querySelectorAll(`.${HAS_PENDING_CHANGES_CLASS}`);

  changedRows.forEach((row) => {
    const lotContainer = row.querySelector(VISIBLE_LOT_RESET_CONTAINER);
    if (!lotContainer) return;

    lotContainer.querySelectorAll(LOT_RESET_QUANTITY_SELECTOR).forEach((input) => {
      const val = parseFloat(input.value);
      if (isNaN(val) || val < 0) {
        displayError(input, "Must be zero or a positive number");
        firstInvalid ??= input;
      }
    });
  });

  if (firstInvalid) {
    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  return firstInvalid === null;
};

const validateResetRequiresLots = () => {
  const changedRows = document.querySelectorAll(`.${HAS_PENDING_CHANGES_CLASS}`);
  let firstProblem = null;

  changedRows.forEach((row) => {
    const hasLots = row.querySelector("[data-lot-code-reset-by-lot]");
    if (hasLots) return;

    firstProblem ??= row;
  });

  if (firstProblem) {
    Flash.error(
      "Some items need lot codes before resetting. Click 'lot codes required' for instructions."
    );
    firstProblem.scrollIntoView({ behavior: "smooth", block: "center" });
    return false;
  }

  return true;
};

export const initializeLotCodesRequiredPrompt = (context) => {
  once(document.body, "lot-codes-required-prompt", (body) => {
    body.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-lot-codes-required-prompt]");
      if (!trigger) return;
      e.preventDefault();

      const addPageUrl = trigger.dataset.addPageUrl;
      const label = context.userFacingText.inventoryTypeLabel || "RM";
      const message = `
        <p>This item doesn't have lot codes yet.</p>
        <p>To reset by lot code:</p>
        <ol>
          <li>Go to "Add to ${label}" and add a small quantity (e.g. 1) with a lot code</li>
          <li>Come back to "Reset ${label}" and use "expand lots" to set the correct quantity</li>
        </ol>
        <p>This is needed because "Require lot codes" is enabled for your account.</p>
      `;

      Confirm.confirm(message, {
        confirm: "Go to Add page",
        cancel: "Cancel",
        html: true,
      }).then((result) => {
        if (result && result.response) {
          window.location.href = addPageUrl;
        }
      });
    });
  });
};

// Handle unsaved changes being cancelled, in which case we pop open the floating widget
// so they can save their changes.
export const onUnsavedChangesCancelled = () => {
  stopSpinner();
  const floatingComponent = document.getElementById("floating-component");
  const floatingFormToggle = document.getElementById("floating-form-toggle");
  if (!floatingComponent || !floatingFormToggle) return;

  floatingComponent.style.display = "";
  floatingComponent.style.visibility = "";
  floatingFormToggle.style.display = "";
  floatingFormToggle.style.visibility = "";

  floatingComponent.classList.add("is-active");
  floatingFormToggle.classList.add("is-active");

  document.dispatchEvent(new CustomEvent("floating-toggle-clicked"));
};

// Helper to allow table_controls.js to check state of hasUnsavedChanges
export const checkHasUnsavedChanges = () => hasUnsavedChanges;
